import React from 'react';
import {
    MapPinIcon,
    ShareIcon,
    HomeIcon
} from '@heroicons/react/24/outline';

const PublicTourCard = ({
    tour,
    t,
    i18n,
    handleShare,
    handleWhatsAppShare,
    copiedId,
    cities,
    propertyTypes,
    defaultCurrency
}) => {
    return (
        <div className="bg-surface-glass backdrop-blur-xl rounded-2xl shadow-card hover:shadow-glow-lg transition-all duration-300 border border-white/20 overflow-hidden group">
            {/* Thumbnail */}
            <div className="relative aspect-[4/3] bg-surface-soft overflow-hidden">
                {/* Sold Stamp */}
                {tour.status === 'sold' && (
                    <div className="absolute top-4 right-4 z-10 transform rotate-12 border-4 border-danger-600 text-danger-600 font-bold text-xl px-4 py-1 rounded opacity-90 bg-white/80">
                        {t('sold')}
                    </div>
                )}

                <a
                    href={`/view-tour/${tour.id}?lang=${i18n.language}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full h-full"
                >
                    {tour.thumbnail_path ? (
                        <img
                            src={`${import.meta.env.VITE_API_URL || ''}/static/${tour.thumbnail_path}`}
                            alt={tour.title}
                            className={`w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500 ${tour.status === 'sold' ? 'grayscale' : ''}`}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-text-muted">
                            <HomeIcon className="h-16 w-16" />
                        </div>
                    )}
                </a>

                {/* Status Badge */}
                {tour.status !== 'active' && (
                    <div className={`absolute top-3 left-3 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg
                        ${tour.status === 'sold' ? 'bg-danger-500 text-white' :
                            tour.status === 'reserved' ? 'bg-warning-500 text-white' :
                                'bg-gray-500 text-white'}`}>
                        {t(`status${tour.status.charAt(0).toUpperCase() + tour.status.slice(1)}`)}
                    </div>
                )}

                {/* Price Badge */}
                {tour.show_price && tour.price && (
                    <div className="absolute bottom-3 right-3 px-3 py-1.5 bg-bg-card/95 backdrop-blur-sm rounded-lg text-sm font-bold text-gold shadow-lg border border-gold/20">
                        {tour.price.toLocaleString()} {t(`currency${tour.currency || defaultCurrency || 'USD'}`)}
                    </div>
                )}

                {/* Share Button */}
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        handleShare(tour.id);
                    }}
                    className="absolute top-3 right-3 p-2.5 bg-bg-card/90 hover:bg-bg-card backdrop-blur-sm rounded-full text-gold hover:text-white transition-all shadow-lg z-10 min-w-[44px] min-h-[44px] flex items-center justify-center border border-gold/20"
                    title={t('copyLink')}
                >
                    {copiedId === tour.id ? (
                        <svg className="h-5 w-5 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    ) : (
                        <ShareIcon className="h-5 w-5" />
                    )}
                </button>
            </div>

            {/* Content */}
            <div className="p-5">
                <h3 className="font-bold text-text text-lg mb-3 line-clamp-1" title={tour.title}>
                    {tour.title}
                </h3>

                <div className="space-y-3 text-sm text-text-muted mb-4">
                    <div className="flex items-center gap-2">
                        <MapPinIcon className="h-4 w-4 flex-shrink-0 text-text-muted" />
                        <span className="line-clamp-1">
                            {(() => {
                                const cityObj = cities?.find(c => c.en.toLowerCase() === tour.city?.toLowerCase());
                                const subareaObj = cityObj?.subareas?.find(s => s.en.toLowerCase() === tour.area?.toLowerCase());
                                const translatedCity = i18n.language === 'ar' ? (cityObj?.ar || tour.city) : (cityObj?.en || tour.city);
                                const translatedArea = tour.area 
                                    ? (i18n.language === 'ar' ? (subareaObj?.ar || tour.area) : (subareaObj?.en || tour.area)) 
                                    : '';
                                return translatedArea 
                                    ? (i18n.language === 'ar' ? `${translatedCity}، ${translatedArea}` : `${translatedCity}, ${translatedArea}`) 
                                    : translatedCity;
                            })()}
                        </span>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-gold/10">
                        <div className="flex gap-4 text-text-muted">
                            {tour.rooms && <span className="flex items-center gap-1"><span className="font-semibold">{tour.rooms}</span> {t('beds')}</span>}
                            {tour.size && <span className="flex items-center gap-1"><span className="font-semibold">{tour.size}</span> m²</span>}
                        </div>
                        <div className="text-gold font-medium text-xs bg-gold/10 px-2 py-1 rounded border border-gold/20">
                            {/* Dynamic Property Type Translation */}
                            {propertyTypes && propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type?.toLowerCase())
                                ? (i18n.language === 'ar' 
                                    ? propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).ar 
                                    : (i18n.language === 'es' && propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).es 
                                        ? propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).es 
                                        : propertyTypes.find(pt => pt.en.toLowerCase() === tour.property_type.toLowerCase()).en))
                                : tour.property_type}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                    <a
                        href={`/view-tour/${tour.id}?lang=${i18n.language}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full py-3 bg-gold-gradient text-white border-none text-center rounded-xl font-medium transition-all shadow-glow hover:shadow-glow-lg hover:scale-[1.02] min-h-[44px] flex items-center justify-center transform"
                    >
                        {tour.file_path || tour.tour_url ? t('view3DTour') : t('viewImages')}
                    </a>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            handleWhatsAppShare(tour);
                        }}
                        className="flex items-center justify-center gap-2 w-full py-3 bg-success-500 hover:bg-success-600 text-white text-center rounded-lg font-medium transition-colors shadow-sm hover:shadow min-h-[44px]"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                        {t('shareOnWhatsApp')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PublicTourCard;
